<template>
  <Grid gtc="200px 1fr" class="approve-invoices overflow-hidden relative-position">
    <template v-if="quickViewItems">
      <ModalSidebar ref="modalSidebar" :items="quickViewItems" @item-selected="openQuickView" />

      <Grid gtr="1fr auto" class="overflow-hidden">
        <ModalBody>
          <div v-if="quickView" :key="quickView.id" class="q-pa-lg bg-white-1 shadow3 border-radius-sm">
            <div class="text-heading-1 text-secondary-2">{{ quickView.provider.name }}</div>
            <InvoiceForm
              :key="reRenderInvoiceForm"
              ref="invoiceForm"
              :quick-view="quickView"
              class="q-mt-lg"
              @currency-changed="selectedCurrency = $event"
              @amount-changed="amount = $event"
            />

            <div
              v-if="selectedSubsCount && amount"
              class="amount-comparison q-mt-lg q-pa-md border-radius-sm text-heading-3 text-secondary-2"
              :class="{ matches: amountComparison.matches }"
            >
              {{ amountComparisonText }}
            </div>

            <MatchingSubscriptionsTable
              ref="matchingSubsTable"
              :subscription-periods="quickView.subscription_periods"
              class="q-mt-lg"
              :currency="selectedCurrency"
              @prices-sum="pricesSumChanged"
              @selected-subs-count="selectedSubsCount = $event"
            />
            <AllSubscriptionsTable :subscription-periods="quickView.subscription_periods" class="q-mt-lg" />
          </div>
        </ModalBody>

        <ModalFooter>
          <RBtn size="big" type="primary" @click="save">Save</RBtn>
        </ModalFooter>
      </Grid>
    </template>
  </Grid>
</template>

<script>
import * as apiTo from '@/modular-api'
import { createFormData } from '@/utils/create-form-data'
import { checkQuickView, getTaskData, openQuickView, setSubtitle } from '@/reusable/workflow/task-modals/modals-shared'
// Components
import ModalSidebar from '../components/ModalSidebar'
import ModalBody from '../components/ModalBody'
import ModalFooter from '../components/ModalFooter'
import InvoiceForm from './components/InvoiceForm'
import AllSubscriptionsTable from './components/all-subscriptions-table/AllSubscriptionsTable'
import MatchingSubscriptionsTable from './components/matching-subscriptions-table/MatchingSubscriptionsTable'

export default {
  components: {
    ModalSidebar,
    ModalBody,
    ModalFooter,
    InvoiceForm,
    AllSubscriptionsTable,
    MatchingSubscriptionsTable
  },

  props: {
    periodId: [Number, String]
  },

  data() {
    return {
      quickViewItems: null,
      quickView: null,
      selectedCurrency: null,
      pricesSum: 0,
      selectedSubsCount: 0,
      amount: 0,
      reRenderInvoiceForm: 0
    }
  },

  computed: {
    amountComparison() {
      const amount = Number(this.amount) // because amount can be null
      const matches = amount.toFixed(2) === this.pricesSum.toFixed(2)
      const lower = this.pricesSum < amount
      const higher = this.pricesSum > amount

      return { matches, lower, higher }
    },

    amountComparisonText() {
      const { matches, higher, lower } = this.amountComparison

      let text = ''
      if (matches) text = 'matches'
      else if (higher) text = `is higher than`
      else if (lower) text = 'is lower than'

      return `The amount ${this.formatPrice(this.pricesSum).replace('.00', '')} ${text} the invoice amount.`
    }
  },

  watch: {
    quickViewItems: 'setSubtitle'
  },

  created() {
    this.getTaskData('approve-invoices').then(({ data }) => (this.quickViewItems = data.result))
  },

  methods: {
    getTaskData,
    checkQuickView,
    setSubtitle: setSubtitle('Vendors'),
    openQuickView: openQuickView('approve-invoices', (vm, data, quickViewItem) => {
      vm.quickView = { id: quickViewItem.id, ...data }
    }),

    getCount() {
      return this.quickViewItems.reduce((acc, curr) => {
        acc += curr.subscriptions_count
        return acc
      }, 0)
    },

    pricesSumChanged(newPrice) {
      this.pricesSum = newPrice
      this.$refs.invoiceForm.setAmount(newPrice)
    },

    save() {
      const formData = new FormData()
      const invoice = this.$refs.invoiceForm.getInvoice()
      const payload = {
        ...invoice,
        subscription_periods: this.$refs.matchingSubsTable.getSelectedSubsIds()
      }

      createFormData(formData, payload)

      this.$emit('set-loader', true)

      apiTo
        .PROCESS_TASK('approve-invoices', this.quickView.provider.id, formData)
        .then(this.checkAfterSave)
        .catch(() => this.$emit('set-loader', false))
    },

    checkAfterSave() {
      const quickViewSubs = this.quickView.subscription_periods.length
      const selectedSubs = this.$refs.matchingSubsTable.getSelectedSubsIds().length
      const allSubsAreSent = quickViewSubs === selectedSubs

      if (allSubsAreSent) this.checkQuickView()
      else this.reFetchQuickView()
    },

    reFetchQuickView() {
      apiTo
        .GET_TASK_QUICK_VIEW('approve-invoices', this.quickView.id)
        .then(({ data }) => {
          this.quickView.subscription_periods = data.result.subscription_periods
          this.reRenderInvoiceForm++

          // Update`subscription_count` in quickViewItems
          const quickViewItemIndex = this.quickViewItems.findIndex(q => q.id === this.quickView.id)
          if (~quickViewItemIndex) {
            this.quickViewItems[quickViewItemIndex].subscriptions_count = data.result.subscription_periods.length
          }
        })
        .finally(() => this.$emit('set-loader', false))
    }
  }
}
</script>

<style lang="scss" scoped>
.approve-invoices {
  .amount-comparison {
    background: var(--r-warning-1);

    &.matches {
      background: var(--r-success-1);
    }
  }
}
</style>
